/**
 * The containment forest with a level of type groups above its roots.
 *
 * Edge bundling routes along the hierarchy, so it only says something when there is a hierarchy to
 * route along. Containment in a viewpoint usually gives it nothing: applications, the people who
 * own them and the modules they contain arrive from different workspaces and are related by
 * reference, not by parentage, so every node is a root, every bundle path is `[source, target]` -
 * two points, which is a straight line - and the layout draws the ball of chords its own docstring
 * exists to avoid.
 *
 * Grouping the roots by type gives the routes an interior point to converge on. Two consequences,
 * and both are the point rather than side effects: every reference between the same two types now
 * shares the middle of its path, which is what makes a bundle a bundle; and the leaves land on the
 * sphere in depth-first order, which puts each type's components in one contiguous band instead of
 * scattering its color over the whole surface.
 *
 * Type is the grouping and not something cleverer - community detection would find groups the data
 * supports better - because type is already the color channel and already in the legend. A reader
 * can see which band is which without being told, and a band that disagrees with the legend is a
 * bug they can point at.
 */

import { toHierarchy, type Hierarchy } from '../graph/topology';
import { toTypesByFrequency } from '../graph/typeOrder';
import type { NetworkNode, NetworkNodeId } from '../types';

export type TypeGroupedHierarchy = {
  hierarchy: Hierarchy;
  /**
   * Size of the index space the hierarchy spans: the real nodes, then one group per type.
   *
   * Groups are appended rather than interleaved, so every real node keeps the index it had. That is
   * what lets `links` stay valid against the grouped hierarchy and lets a caller cut the group
   * positions back off with a `slice` - `NetworkLayout.positions` is parallel to `graph.nodes`, and
   * a group is a control point rather than something drawn.
   */
  indexCount: number;
  /**
   * The groups, in band order, or empty when nothing was grouped.
   *
   * What the sphere sizes its sectors by. Empty rather than absent, so a caller that grouped nothing
   * and a caller that grouped something ask the same question of the result.
   */
  groupIndices: number[];
};

/**
 * A prefix no real id starts with, so a group cannot collide with a component.
 *
 * Grown rather than assumed unique. Ids come from the host and type names come from whoever
 * configured the model, so "nothing is called `type:Application`" is a guess about someone else's
 * data - and a collision would quietly re-parent a real component under a group, which draws as a
 * believable picture of an estate that does not exist.
 */
const toGroupIdPrefix = (nodes: readonly NetworkNode[]): string => {
  let prefix = 'type:';

  const collides = (): boolean => nodes.some(({ id }) => id.startsWith(prefix));

  while (collides()) {
    prefix = `_${prefix}`;
  }

  return prefix;
};

const toGroupNode = (
  typeName: string,
  prefix: string
): NetworkNode & { id: NetworkNodeId } => ({
  id: `${prefix}${typeName}`,
  /* The group's own id: a synthetic type group is not a thing the host can recognise again, so it
     has no identity beyond this arrangement. */
  matchKey: `${prefix}${typeName}`,
  label: typeName,
  typeName,
  fill: null,
  parentId: null,
  /* Never drawn, so it needs no size. A group with a weight would take a radius from the same
     square root every node does, and the renderer would have a mark with no component behind it. */
  weight: 0,
});

/**
 * Whether `toHierarchy` made this node a root because of its own `parentId`.
 *
 * Read off the hierarchy rather than re-derived from `parentId`, so the two cannot disagree about
 * what an unresolvable or self-referential parent means. Deliberately *not* the same question as
 * `rootIndices.includes` - that list also holds the nodes a cycle left unreachable, whose
 * `parentId` still points into their cycle. Re-parenting one of those would leave it both a child
 * of its cycle and a child of a group, which double-counts it in every leaf total.
 */
const isParentlessRoot = ({ parentIndex }: Hierarchy, index: number): boolean =>
  parentIndex[index] === null || parentIndex[index] === index;

/**
 * Inserts the group level, or returns the hierarchy untouched when there is nothing to group by.
 *
 * Only the types that have a root to collect get a group. A type whose every component is contained
 * by something - modules that are all children of applications - has nothing to re-parent, and an
 * empty group is not harmless: a group with no children is a leaf, so it takes a slot on the leaf
 * spiral and leaves a gap in the band where no node is drawn.
 *
 * Fewer than two groups is not a grouping. One group is a single ancestor over the whole estate,
 * which sends every route through one interior point - the hairball `innerRadiusFraction` documents
 * rather than an improvement on straight chords.
 */
export const toTypeGroupedHierarchy = (
  nodes: readonly NetworkNode[],
  hierarchy: Hierarchy
): TypeGroupedHierarchy => {
  const rootTypeNames = new Set(
    nodes
      .filter((_, index) => isParentlessRoot(hierarchy, index))
      .map(({ typeName }) => typeName)
  );
  /* Ordered over the whole node list rather than over the roots, so the bands stay in the order the
     legend numbers its types by - which is the count of components of that type, contained or not. */
  const typeNames = toTypesByFrequency(nodes).filter(typeName =>
    rootTypeNames.has(typeName)
  );

  if (typeNames.length < 2) {
    return { hierarchy, indexCount: nodes.length, groupIndices: [] };
  }

  const prefix = toGroupIdPrefix(nodes);
  const groups = typeNames.map(typeName => toGroupNode(typeName, prefix));
  const groupIdOf = new Map(groups.map(({ typeName, id }) => [typeName, id]));

  /* Rebuilt through `toHierarchy` rather than patched in index space. Depths, leaf counts and the
     cycle handling all follow from `parentId`, and transcribing them here would be a second copy of
     that arithmetic to keep in step. */
  const grouped: NetworkNode[] = [
    ...nodes.map((node, index) =>
      isParentlessRoot(hierarchy, index)
        ? { ...node, parentId: groupIdOf.get(node.typeName) ?? null }
        : node
    ),
    ...groups,
  ];

  return {
    hierarchy: toHierarchy(grouped),
    indexCount: grouped.length,
    groupIndices: groups.map((_, at) => nodes.length + at),
  };
};
